import { entries, includes } from '@hover/utilities';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AiGeneratedImageSegmentationQuery } from 'src/shared/graphql/generated';
import { isBrowser } from 'src/shared/utils/environment';

import type { Region } from '../../constants/customizationCategories';
import { processMaskSvg } from '../../helpers/segmentation';
import type { SegmentationSurface } from '../../schemas/segmentation';

import { useSegmentMaterials } from './SegmentationOverlay';
import type {
  HandleFunctionsRef,
  HandlePlacementMode,
} from './SegmentationOverlay';

export const HANDLE_PLACEMENT_MODE =
  'material' as const satisfies HandlePlacementMode;

export const SELECTION_MODES = ['region', 'instance'] as const;
export const [SELECTION_MODE_REGION, SELECTION_MODE_INSTANCE] = SELECTION_MODES;

export type SelectionMode = (typeof SELECTION_MODES)[number];

export const REGION_SURFACE_MAPPING = {
  walls: 'Wall',
} satisfies Partial<Record<Region, SegmentationSurface>>;

const supportedRegions = entries(REGION_SURFACE_MAPPING).map(
  ([region]) => region,
);

export const regionToSurface = (region: Region) =>
  includes(supportedRegions, region) ? REGION_SURFACE_MAPPING[region] : null;

export interface UseSegmentationOptions {
  region: Region | null | undefined;
  query: AiGeneratedImageSegmentationQuery | undefined;
}

export const useSegmentation = ({
  region = null,
  query,
}: UseSegmentationOptions) => {
  const handlePlacementMode = HANDLE_PLACEMENT_MODE;

  const surface = region ? regionToSurface(region) : null;
  const svg = query?.aiGeneratedImage?.aiSegmentation?.instanceSvg;

  // Selection is represented as a list of either instance IDs
  // or material names, depending on the handle placement mode.
  const [selection, setSelection] = useState<string[]>([]);
  const [selectionRegion, setSelectionRegion] = useState<Region | null>(null);

  if (selectionRegion && region !== selectionRegion) {
    setSelection([]);
    setSelectionRegion(null);
  }

  // Filter the segmentation data to the currently selected surface (which is
  // mapped to the top-level region) before passing it down to the overlay
  const data = useMemo(() => {
    const processed = svg && isBrowser() ? processMaskSvg(svg) : null;

    return processed
      ? {
          ...processed,
          segments: processed.segments.filter(
            segment => segment.surface === surface,
          ),
        }
      : null;
  }, [surface, svg]);

  const { segments } = data ?? {};

  // The segmentation instances that represent the current selection, regardless
  // of whether we're selecting by instance or material. This is what gets
  // passed to the backend and to the selection highlight interface.
  const selectedSegments = useMemo(() => {
    if (!segments) return null;

    return segments.filter(segment =>
      handlePlacementMode === 'material'
        ? selection.includes(segment.material)
        : selection.includes(segment.id),
    );
  }, [handlePlacementMode, segments, selection]);

  const updateSelection = useCallback(
    (value: string[]) => {
      setSelectionRegion(region);
      setSelection(value);
    },
    [region],
  );

  const resetSelection = useCallback(() => {
    setSelectionRegion(null);
    setSelection([]);
  }, []);

  const allMaterials = useSegmentMaterials(segments ?? []);

  // All selectable items. In instance mode, this is all instances for the
  // surface and in material mode this is all materials for the surface.
  const availableSelection = useMemo(() => {
    if (!segments) return [];

    return handlePlacementMode === 'material'
      ? allMaterials
      : segments.map(({ id }) => id);
  }, [allMaterials, handlePlacementMode, segments]);

  const selectAll = useCallback(() => {
    updateSelection(availableSelection);
  }, [availableSelection, updateSelection]);

  const selectNone = useCallback(() => updateSelection([]), [updateSelection]);

  // We apply edits in "region" mode until the user makes an instance-level
  // selection, at which point we switch to "instance" mode and require an
  // instance selection in order to apply edits. We update `selectionRegion`
  // when an instance-level selection is made so this returns "region" for
  // the current region until then.
  const selectionMode = useMemo(
    () =>
      selectionRegion && region === selectionRegion
        ? SELECTION_MODE_INSTANCE
        : SELECTION_MODE_REGION,
    [region, selectionRegion],
  );

  const areAllSelected = useMemo(
    () =>
      selectionMode === SELECTION_MODE_REGION ||
      selection.length === availableSelection.length,
    [availableSelection, selection, selectionMode],
  );

  const toggleSelectAll = useCallback(() => {
    if (areAllSelected) {
      selectNone();
    } else {
      selectAll();
    }
  }, [areAllSelected, selectAll, selectNone]);

  const [isTooltipOpen, setIsTooltipOpen] = useState(true);

  useEffect(() => {
    if (selection.length > 0) setIsTooltipOpen(false);
  }, [selection.length]);

  const handleFunctionsRef = useRef<HandleFunctionsRef>(null);

  const showSelectionRequired = useCallback(() => {
    setIsTooltipOpen(true);
    handleFunctionsRef.current?.pulse();
  }, []);

  return {
    areAllSelected,
    data,
    handleFunctionsRef,
    handlePlacementMode,
    hasSegmentation: availableSelection.length > 1,
    isTooltipOpen,
    selectedSegments,
    selection,
    selectionMode,
    setSelection: updateSelection,
    resetSelection,
    showSelectionRequired,
    surface,
    toggleSelectAll,
  };
};

export type Segmentation = ReturnType<typeof useSegmentation>;
export type CustomizationSegmentation = Omit<
  Segmentation,
  'handleFunctionsRef'
>;
